import classNames from 'classnames'
import Link from 'next/link'

import Icon from '@/components/Icon/Icon'
import InteractiveTooltip from '@/components/InteractiveTooltip/InteractiveTooltip'
import Mana from '@/components/Mana/Mana'
import { CardLabelVariantType, GenericCardType, PageCardType } from '@/types/card'

import styles from './CardLabel.module.scss'

const LINK_FAQ_SYNERGY = '/faq?q=1'
// const LINK_FAQ_LIFT = '/faq'

const formatSigFig = (num: number) => {
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(2).replace(/\.0+$/, '') + 'M'

  if (num >= 100_000) return Math.round(num / 1_000) + 'K'

  if (num >= 10_000) return (num / 1_000).toFixed(1).replace(/\.0+$/, '') + 'K'

  if (num >= 1_000) return (num / 1_000).toFixed(2).replace(/\.0+$/, '') + 'K'

  return num.toString()
}

// const formatLift = (lift: number) => (lift > 99 ? '99+' : lift.toPrecision(Math.round(lift) < 1 ? 2 : 3))

const formatSynergy = (synergy: number) => `${(synergy * 100).toFixed(0)}%`

const formatInclusion = (numDecks: number, potentialDecks: number, numeralsInput: number = 2) => {
  if (potentialDecks <= 0) return null
  const numerals = numeralsInput >= 2 && numeralsInput <= 5 ? numeralsInput : 2
  const inclusion = (numDecks / potentialDecks) * 100
  const inclusionRounded = Math.round(inclusion)
  let decimals = inclusionRounded >= 100 ? numerals - 3 : inclusionRounded >= 10 ? numerals - 2 : numerals - 1
  decimals = decimals > 0 ? decimals : 0

  return `${inclusion.toFixed(decimals)}%`
}

const CardLabelSalt = ({ card }: { card?: GenericCardType }) => {
  // TODO: replace with real thing
  const salt = card?.label?.split(' ')?.[2]?.split('\n')?.[0]

  if (!salt) return null

  return (
    <div className={styles.salt}>
      <span>Salt Score: {salt}</span>
    </div>
  )
}

const CardLabelTotal = ({ card, prefix }: { card?: GenericCardType; prefix?: string }) => {
  const numDecks = card?.num_decks

  if (numDecks === undefined) return null

  return (
    <div className={styles.line}>
      {prefix && <span>{prefix}</span>}
      <span>{numDecks.toLocaleString()}</span>
      <span>decks</span>
    </div>
  )
}

const CardLabelInclusion = ({ card, numerals }: { card?: GenericCardType; numerals?: 2 | 3 }) => {
  const numDecks = card?.num_decks
  const potentialDecks = card?.potential_decks

  if (numDecks === undefined || potentialDecks === undefined) return null

  const inclusion = formatInclusion(numDecks, potentialDecks, numerals)

  const numDecksFormatted = formatSigFig(numDecks)
  const potentialDecksFormatted = formatSigFig(potentialDecks)

  return potentialDecks > 0 ? (
    <InteractiveTooltip
      placement='bottom'
      tooltipChildren={
        <div className={styles.line}>
          <span className={classNames(styles.fraction)}>
            <span>
              <strong>{numDecks.toLocaleString()}</strong> decks
            </span>
            <span className={styles.denominator}>
              <strong>{potentialDecks.toLocaleString()}</strong> potential decks
            </span>
          </span>
        </div>
      }
    >
      <div className={styles.line}>
        <span className={styles.stat}>{inclusion}</span>
        <span className={styles.label}>inclusion</span>
        <span>
          <span className={classNames(styles.fraction, 'ms-2')}>
            <span className={styles.numerator}>
              <b>{numDecksFormatted}</b> decks
            </span>
            <span className={styles.denominator}>
              <b>{potentialDecksFormatted}</b> decks
            </span>
          </span>
        </span>
      </div>
    </InteractiveTooltip>
  ) : (
    <div className={styles.line}>
      <span className={styles.stat}>0</span>
      <span className={styles.label}>decks</span>
    </div>
  )
}

const CardLabelRank = ({ card }: { card?: GenericCardType }) => {
  const rank = card?.rank?.toLocaleString()
  if (rank === undefined) return null

  return (
    <div className={styles.line}>
      <span className={styles.rank}>Rank #{rank}</span>
    </div>
  )
}

const CardLabelLift = ({ card, pageCommander }: { card?: GenericCardType; pageCommander?: PageCardType }) => {
  // TODO: Add back in when lift is ready!
  /*
  const lift = card?.lift

  if (lift !== undefined) {
    const liftFormatted = formatLift(lift)
    let arrowsDown = 0
    let arrowsUp = 0
    if (lift < 0.3) arrowsDown = 2
    else if (lift < 0.7) arrowsDown = 1
    else if (lift > 20) arrowsUp = 2
    else if (lift > 3) arrowsUp = 1

    const arrows = (
      <>
        {Array(arrowsDown).fill(<Icon icon='arrowDown' />)}
        {Array(arrowsUp).fill(<Icon icon='arrowUp' />)}
      </>
    )

    return (
      <InteractiveTooltip
        tooltipChildren={
          <Link className={styles.line} href={LINK_FAQ_LIFT} target='_blank'>
            <span>Lift is a measurement of foo bar baz bingus dingus lingus.</span>
            <span className={styles.iconRight}>
              <Icon icon='circleQuestion' />
            </span>
          </Link>
        }
      >
        <Link className={styles.line} href={LINK_FAQ_LIFT} target='_blank'>
          {arrows && <span className={styles.iconLeft}>{arrows}</span>}
          <span className={styles.stat}>{liftFormatted}</span>
          <span className={classNames(styles.label, styles.underline)}>lift</span>
        </Link>
      </InteractiveTooltip>
    )
  }
  */

  // TODO: Remove once replaced with lift
  const synergy = card?.synergy
  const numDecks = card?.num_decks
  const potentialDecks = card?.potential_decks

  if (synergy === undefined || numDecks === undefined || potentialDecks === undefined) return null

  const synergyFormatted = formatSynergy(synergy)
  let arrowsUp = 0
  if (synergy > 0.6) arrowsUp = 2
  else if (synergy > 0.2) arrowsUp = 1

  const arrows = Array(arrowsUp).fill(<Icon icon='arrowUp' />)

  let content = (
    <Link className={styles.line} href={LINK_FAQ_SYNERGY} target='_blank'>
      {arrows && <span className={styles.iconLeft}>{arrows}</span>}
      <span className={styles.statSmall}>{synergyFormatted}</span>
      <span className={classNames(styles.label, styles.underline)}>synergy</span>
    </Link>
  )

  if (pageCommander !== undefined) {
    content = (
      <InteractiveTooltip
        placement='bottom'
        tooltipChildren={
          <Link className={styles.synergyTooltipBody} href={LINK_FAQ_SYNERGY} target='_blank'>
            <span>synergy = </span>
            <span>
              <div>
                usage in {pageCommander.name.split(' // ')[0]} ({Math.round((100 * numDecks) / potentialDecks)}
                %)
              </div>
              <div>
                - usage in <Mana colors={pageCommander.color_identity} /> (
                {Math.round(Math.round((100 * numDecks) / potentialDecks) - 100 * synergy)}
                %)
              </div>
            </span>
          </Link>
        }
      >
        {content}
      </InteractiveTooltip>
    )
  }

  return content
}

type Props = {
  card?: GenericCardType
  labelVariant?: CardLabelVariantType
  pageCommander?: PageCardType
}

const CardLabel = ({ card, labelVariant, pageCommander }: Props) => {
  let labels = null

  switch (labelVariant) {
    case 'directory-count':
      {
        labels = (
          <>
            <CardLabelTotal card={card} />
          </>
        )
      }
      break
    case 'directory-inclusion':
      {
        labels = (
          <>
            <CardLabelInclusion card={card} />
          </>
        )
      }
      break
    case 'directory-rank':
      {
        labels = (
          <>
            <CardLabelRank card={card} />
            <CardLabelTotal card={card} />
          </>
        )
      }
      break
    case 'featured': {
      if (pageCommander?.is_commander) {
        labels = (
          <>
            <CardLabelRank card={card} />
            <CardLabelTotal card={card} />
          </>
        )
      } else {
        labels = (
          <>
            <CardLabelInclusion card={card} numerals={3} />
          </>
        )
      }
      break
    }
    case 'rec': {
      labels =
        card?.num_decks !== undefined && card?.potential_decks !== undefined ? (
          <>
            <CardLabelInclusion card={card} />
            <CardLabelLift card={card} pageCommander={pageCommander} />
          </>
        ) : (
          <CardLabelTotal card={card} />
        )
      break
    }
    case 'salt': {
      labels = (
        <>
          <CardLabelSalt card={card} />
          <CardLabelTotal card={card} />
        </>
      )
      break
    }
    default: {
      const label = card?.label
      if (label) labels = <div className={styles.defaultLabel}>{label}</div>
    }
  }

  return <div className={styles.container}>{labels}</div>
}

export default CardLabel
